import React, { FC, useEffect, useCallback } from 'react';
import { useStore, useDispatch, useSelector } from 'react-redux';
import Collapse from 'antd/lib/collapse';

import { config, loadScriptAsync, format } from '@/utils';
import { Title, Icon } from '@/apps/appCreator/components';
import { getCreditCalcOpts, getCreditCalcFormState } from '@/selectors';
import { TBrandCar } from '@/selectors/getBrandCar';
import {
  actions as actionsCreditCalc,
  selectors as selectorsCreditCalc,
} from '@/apps/appCreator/store/creditCalc';
import { Disclaimers, TSortedDisclaimers } from '@/selectors/getCarDisclaimers';

import style from './style.module.scss';

type TProps = {
  car: TBrandCar;
  disclaimers: TSortedDisclaimers;
};

const initCreditData = {
  initialPayment: {
    value: 50
  },
  programType: {
    value: 2
  },
  deferredPayment: {
    value: Number.MAX_SAFE_INTEGER,
  }
};

const CarCreditCalc: FC<TProps> = ({ car, disclaimers }) => {
  const dispatch = useDispatch();
  const { loaded: creditCalcLoaded } = useSelector(
    selectorsCreditCalc.selectState
  );

  const store = useStore();
  const selectorId = 'credit-calc';
  const carCreditCalcOpts = getCreditCalcOpts(store.getState(), car);
  const { suzukiFinanceDiscount } = car?.extra || {};

  const onUpdateCreditCalc = (data: any) => {
    dispatch(actionsCreditCalc.updateFormState(data));
  };

  const initCreditCalc = (opts: { [K: string]: any } = initCreditData) => {
    window.creditCalc.create({
      selectorId,
      features: {
        integrationCarStock: 'yes',
      },
      subscribe: onUpdateCreditCalc,
      car: {
        ...carCreditCalcOpts,
        ...opts,
      },
    });
  };

  const getIconContent = useCallback(() => <Icon name="circled-angle" />, []);

  // Перезапускаем КК если только изменился первоначальный взнос
  useEffect(() => {
    if (carCreditCalcOpts && creditCalcLoaded) {
      initCreditCalc();
    }
  }, [carCreditCalcOpts?.initialPayment?.value, creditCalcLoaded]);

  /**
   * Перезапускаем КК если только
   * 1) изменилась цена авто (с учетом выгод),
   * 2) изменился срок кредитования в самом КК
   * 3) изменилась модификация
   */
  useEffect(() => {
    if (carCreditCalcOpts && creditCalcLoaded) {
      initCreditCalc();
    }
  }, [
    carCreditCalcOpts?.modification?.value,
    carCreditCalcOpts?.price?.value,
    creditCalcLoaded,
  ]);

  useEffect(() => {
    if (!creditCalcLoaded) {
      loadScriptAsync(config.get('creditCalcSrcScript'), () =>
        dispatch(actionsCreditCalc.success())
      );
    }
  }, []);

  return (
    <div className="mb-5">
      <link href={config.get('creditCalcSrcCss')} rel="stylesheet" />

      <Collapse
        className="collapse-default-theme"
        expandIcon={getIconContent}
        expandIconPosition="right"
      >
        <Collapse.Panel
          key={1}
          forceRender
          header={
            <Title className="my-2" tag="h2">
              Рассчитайте кредит
              {suzukiFinanceDiscount && (
                <small>
                  Выгода до {format.price(suzukiFinanceDiscount)} руб.
                  <sup>{disclaimers.get(Disclaimers.credit)?.sortOrder}</sup>
                </small>
              )}
            </Title>
          }
        >
          <div id={selectorId} className={style.creditCalc} />
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

export default CarCreditCalc;
